import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';

import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { generalConfigFromGlobal } from '../../../shared/workspace-element-context';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../../shared/efile-grid.directive';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

type SetupTabKey = 'enabled-disabled' | 'unlock-validation' | 'formelements-report';

interface SetupTab {
  key: SetupTabKey;
  label: string;
  legacyStateUrl: string;
  /**
   * Legacy gizmoGrid identifiers:
   *   load  → `loadGridJson?grid_id=…&action_code=…` (EfileElementsController)
   *   export → hidden `#action_id` / `#screen_key` spans in each *.html template
   *     enabled-disabled    → load m7kbob / export action_id 32980, screen 11527
   *     unlock-validation   → load xjcg8f / export action_id 33004, screen 11536
   *     formelements-report → load 70u8yi / export action_id 33037, screen 11550
   */
  legacyGrid: {
    gridId: string;
    actionCode: string;
    exportActionId: number;
    exportScreenKey: number;
  };
}

/**
 * Action wired to a grid callName. The `EnabledDisabledController` and
 * `UnlockValidationController` both fire a single ctrlFn call from a
 * pencil icon. The handler here mirrors that exactly:
 *   1. Show a confirm popup ("Are you sure …?")
 *   2. POST `saveJsonObject?action_code=<save>` with json_data of the row
 *   3. On success, refresh the grid
 */
interface CtrlFnAction {
  callName: string;
  saveActionCode: string;
  confirmMessage: string;
  successMessage: string;
}

const CTRL_FN_ACTIONS: Record<string, CtrlFnAction> = {
  enabledDisabledFormData: {
    callName: 'enabledDisabledFormData',
    saveActionCode: 'w016n5',
    confirmMessage: 'Are you sure you want to Toggle Form Exclusion for Selected Row?',
    successMessage: 'Toggle Exclusion successfully',
  },
  unlockValidationData: {
    callName: 'unlockValidationData',
    saveActionCode: 'rysucw',
    confirmMessage: 'Are you sure you want to Toggle Unlock Validation for Selected Row?',
    successMessage: 'Successfully Toggle Unlock Validation',
  },
};

/**
 * Angular 12 port that consolidates three legacy sibling states into a
 * single tabbed surface (they had no shared parent in the legacy code but
 * live together in the sidebar under "Setup"):
 *
 *   - `submissions-dasboard.submission.enabledDisabled`
 *       ui/app/components/efile/efileElements/enabled-disabled.html
 *       (EnabledDisabledController, ctrlFn `enabledDisabledFormData`)
 *   - `submissions-dasboard.submission.unlockValidation`
 *       ui/app/components/efile/efileElements/unlock-validation.html
 *       (UnlockValidationController, ctrlFn `unlockValidationData`)
 *   - `submissions-dasboard.submission.formElementsReport`
 *       ui/app/components/efile/efileElements/formelements-report.html
 *       (FormElementsReportController, no ctrlFn — read-only grid)
 *
 * Each tab mounts an `efileGrid` with the legacy grid_id / action_code.
 * When the active tab fires a callback, this component dispatches the
 * matching `CtrlFnAction`: pop a confirm dialog, POST `saveJsonObject`,
 * refresh on success — same flow the legacy controllers had.
 */
@Component({
    selector: 'gtw-efile-setup',
    templateUrl: './efile-setup.component.html',
    styleUrls: ['./efile-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileSetupComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Input() initialTab?: SetupTabKey;

  readonly tabs: SetupTab[] = [
    {
      key: 'enabled-disabled',
      label: 'Enabled / Disabled',
      legacyStateUrl: 'submissions-dasboard.submission.enabledDisabled',
      legacyGrid: {
        gridId: '6033',
        actionCode: 'm7kbob',
        exportActionId: 32980,
        exportScreenKey: 11527,
      },
    },
    {
      key: 'unlock-validation',
      label: 'Unlock Validation',
      legacyStateUrl: 'submissions-dasboard.submission.unlockValidation',
      legacyGrid: {
        gridId: '6046',
        actionCode: 'xjcg8f',
        exportActionId: 33004,
        exportScreenKey: 11536,
      },
    },
    {
      key: 'formelements-report',
      label: 'Form Elements Report',
      legacyStateUrl: 'submissions-dasboard.submission.formElementsReport',
      legacyGrid: {
        gridId: '6055',
        actionCode: '70u8yi',
        exportActionId: 33037,
        exportScreenKey: 11550,
      },
    },
  ];

  selectedKey: SetupTabKey = 'enabled-disabled';

  /**
   * Per-tab gridConfig. Built lazily once we know the active submission so
   * the directive only initialises after `extraParams.submission_id` is
   * known. Re-keyed on submission change so cache keys stay scoped per
   * submission (legacy controllers used `EFILE_ENABLE_DISABLE_<id>` etc).
   */
  gridConfig: EfileGridConfig | null = null;

  @ViewChild('setupGrid') gridRef?: EfileGridDirective;

  // Confirm dialog state — uses the shared `gtw-confirmation-modal` so the
  // visual + UX matches every other efile confirm flow (legacy parity:
  // `templates/modals/confirm-action.html`).
  showConfirm = false;
  saving = false;
  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: '',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Saving...',
  };
  private pendingRows: any[] = [];
  private pendingAction: CtrlFnAction | null = null;

  private static readonly JCD_KEY = 250;

  constructor(
    private cdr: ChangeDetectorRef,
    private submissionService: SubmissionService,
    private gtwGlobal: GlobalService,
    private appService: AppService,
    private snackbar: GtwSnackbarService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.buildGridConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTab'] && this.initialTab && this.initialTab !== this.selectedKey) {
      this.selectedKey = this.initialTab;
      this.buildGridConfig();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    // No-op — directive cleans up its own gizmoGrid on destroy.
  }

  // ---------------------------------------------------------------------------
  // Tab navigation
  // ---------------------------------------------------------------------------

  selectTab(key: SetupTabKey): void {
    if (this.selectedKey === key) return;
    this.selectedKey = key;
    // Null first so *ngIf tears down the old efileGrid before the new
    // one mounts. setTimeout pushes the rebuild past Angular's CD pass.
    this.gridConfig = null;
    this.cdr.markForCheck();
    setTimeout(() => this.buildGridConfig(), 0);
  }

  isSelected(key: SetupTabKey): boolean {
    return this.selectedKey === key;
  }

  get selectedTab(): SetupTab | undefined {
    return this.tabs.find((t) => t.key === this.selectedKey);
  }

  hasActiveSubmission(): boolean {
    return !!this.submissionService.activeSubmission?.SUBMISSION_ID;
  }

  // ---------------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------------

  private buildGridConfig(): void {
    const submission = this.submissionService.activeSubmission;
    const tab = this.selectedTab;
    if (!submission?.SUBMISSION_ID || !tab) {
      this.gridConfig = null;
      this.cdr.markForCheck();
      return;
    }

    this.gridConfig = {
      actionCode: tab.legacyGrid.actionCode,
      exportActionId: tab.legacyGrid.exportActionId,
      exportScreenKey: tab.legacyGrid.exportScreenKey,
      gridId: tab.legacyGrid.gridId,
      autoLoad: true,
      extraParams: { submission_id: submission.SUBMISSION_ID },
      cacheKey: `efile/setup/${tab.key}/${submission.SUBMISSION_ID}`,
    };
    this.cdr.markForCheck();
  }

  /**
   * gizmoGrid callback. Mirrors `EnabledDisabledController`/`UnlockValidationController`
   * `ctrlFn(_callName, _data, _type)` — both controllers only care about a
   * single callName and route through a confirm-action modal before
   * calling `saveJsonObject`. We do the same here.
   */
  onGridCallback(event: EfileGridCallbackEvent): void {
    if (!event || event.type === 'columnTemplate') return;
    const action = CTRL_FN_ACTIONS[event.callName];
    if (!action) return;

    const rows = Array.isArray(event.data) ? event.data : [];
    if (!rows.length) return;

    this.pendingRows = rows;
    this.pendingAction = action;
    this.confirmConfig = {
      ...this.confirmConfig,
      message: action.confirmMessage,
      loading: false,
    };
    this.showConfirm = true;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Confirm dialog handlers
  // ---------------------------------------------------------------------------

  onCancelConfirm(): void {
    this.showConfirm = false;
    this.pendingRows = [];
    this.pendingAction = null;
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    if (!this.pendingAction || this.saving) return;

    const action = this.pendingAction;
    const rows = this.pendingRows;

    this.saving = true;
    this.confirmConfig = { ...this.confirmConfig, loading: true };
    this.cdr.markForCheck();

    this.runSaveAction(action, rows).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.saving = false;
        this.showConfirm = false;
        this.confirmConfig = { ...this.confirmConfig, loading: false };
        const ok =
          String(data?.callSuccess) === '1' &&
          (data?.errorMessage === 'null' || data?.errorMessage == null);

        if (ok) {
          this.snackbar.openSnackBar(action.successMessage, 'gtw-snackbar--green');
          if (this.gridRef) this.gridRef.refresh();
        } else {
          this.snackbar.openSnackBar(
            data?.errorMessage && data.errorMessage !== 'null'
              ? data.errorMessage
              : 'Something went wrong while saving the data!',
            'gtw-snackbar--red',
          );
        }
        this.pendingRows = [];
        this.pendingAction = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving = false;
        this.showConfirm = false;
        this.confirmConfig = { ...this.confirmConfig, loading: false };
        this.snackbar.openSnackBar(
          'Something went wrong while saving the data!',
          'gtw-snackbar--red',
        );
        this.pendingRows = [];
        this.pendingAction = null;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Mirrors the legacy `enabledDisabledForm`/`unlockValidationForm`
   * functions: builds `json_data` from `_data[i].data` rows, derives the
   * default scenario for the submission's tax year, then POSTs as
   * application/x-www-form-urlencoded to `saveJsonObject?action_code=…`.
   */
  private runSaveAction(action: CtrlFnAction, rows: any[]) {
    const submission = this.submissionService.activeSubmission;
    const baseUrl = generalConfigFromGlobal(this.gtwGlobal).base_url || '/gtw';
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';

    const gridData = rows.map((r: any) => r?.data ?? r);
    const jsonObj = JSON.stringify(gridData);

    const taxYear = submission?.TAX_YEAR;
    const scenario = this.resolveDefaultScenario(taxYear);

    // checkLockedbj mirrors the legacy EnabledDisabledController /
    // UnlockValidationController payload — only scenario_key is needed
    // for these single-submission actions.
    const checkLockedbj = JSON.stringify([{ scenario_key: String(scenario) }]);

    const params: Record<string, any> = {
      json_data: jsonObj,
      tax_year: taxYear,
      scenario,
      jcd_key: EfileSetupComponent.JCD_KEY,
      sso_id: ssoId,
      submission_id: submission?.SUBMISSION_ID,
      checkLockedbj,
      scenarioCode: 'RAF',
      is_issue_key_enabled: 'N',
    };

    const body = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        body.set(key, String(value));
      }
    });

    return this.http.post<any>(
      `${baseUrl}/saveJsonObject?action_code=${action.saveActionCode}`,
      body.toString(),
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        withCredentials: true,
      },
    );
  }

  /**
   * Reproduces legacy lookup:
   *   selectedTaxYear.scenarios.find(s => s.jcd_key == 250 && s.default_scenario === 'Y')
   * (see EnabledDisabledController#enabledDisabledForm).
   */
  private resolveDefaultScenario(taxYear: any): any {
    const values: any[] = (this.gtwGlobal.global as any)?.inputs?.tax_year?.values || [];
    const ty = String(taxYear ?? '');
    const found = _.find(values, { label: ty }) || _.find(values, (v: any) => String(v?.value) === ty);
    const scenarios: any[] = found?.scenarios || [];
    const def = _.find(
      scenarios,
      (s: any) => Number(s?.jcd_key) === EfileSetupComponent.JCD_KEY && s?.default_scenario === 'Y',
    );
    if (def?.scenario != null) return def.scenario;
    return (this.gtwGlobal.global as any)?.globalParams?.scenario;
  }
}
